import cloneDeep from 'lodash/cloneDeep';
import { cast, flow, types } from 'mobx-state-tree';
import { IHouse, ILift, IObject, IPorch, IStandpipe } from 'types';
import { Houses } from 'http-requests';

import { RequestStateModel } from './general-models';

const RequestsModel = types.model({
  houseData: types.optional(RequestStateModel, {}),
  providerHousesById: types.optional(RequestStateModel, {}),
});

const LiftModel = types.model({
  id: types.optional(types.maybeNull(types.string), null),
  desc: types.optional(types.maybeNull(types.string), null),
  number: types.optional(types.maybeNull(types.string), null),
  capacity: types.optional(types.maybeNull(types.number), null),
  started_at: types.optional(types.maybeNull(types.Date), null),
  climb_rate: types.optional(types.maybeNull(types.number), null),
  well_type: types.optional(types.maybeNull(types.string), null),
  stop_number: types.optional(types.maybeNull(types.number), null),
  manufacturer: types.optional(types.maybeNull(types.string), null),
  has_freq_reg: types.optional(types.maybeNull(types.boolean), null),
  deadline_years: types.optional(types.maybeNull(types.number), null),
  modernization_date: types.optional(types.maybeNull(types.Date), null),
  norm_durability: types.optional(types.maybeNull(types.string), null),
});

const StandpipeModel = types.model({
  id: types.optional(types.maybeNull(types.string), null),
  x: types.optional(types.maybeNull(types.array(types.number)), null),
  y: types.optional(types.maybeNull(types.array(types.number)), null),
  desc: types.optional(types.maybeNull(types.string), null),
  number: types.optional(types.maybeNull(types.string), null),
  located_at: types.optional(types.maybeNull(types.string), null),
  water_zone: types.optional(types.maybeNull(types.string), null),
  service_type: types.optional(types.maybeNull(types.string), null),
  type: types.optional(types.maybeNull(types.string), null),
});

const PorchModel = types.model({
  id: types.optional(types.string, ''),
  lifts: types.optional(types.array(LiftModel), []),
  standpipes: types.optional(types.array(StandpipeModel), []),
  min_floor: types.optional(types.maybeNull(types.number), null),
  max_floor: types.optional(types.maybeNull(types.number), null),
  has_lift: types.optional(types.maybeNull(types.boolean), null),
  start_num_floor: types.optional(types.maybeNull(types.number), null),
  number: types.optional(types.maybeNull(types.number), null),
  start_num_area: types.optional(types.maybeNull(types.number), null),
  has_appz: types.optional(types.maybeNull(types.boolean), null),
  pzu: types.optional(types.maybeNull(types.string), null),
  has_chute: types.optional(types.maybeNull(types.boolean), null),
  area_count: types.optional(types.maybeNull(types.number), null),
  build_date: types.optional(types.maybeNull(types.string), null),
  water_zone_limits: types.optional(types.maybeNull(types.frozen()), null),
});

const HouseDataModel = types
  .model({
    OKTMO: types.optional(types.maybeNull(types.string), null),
    address: types.optional(types.string, ''),
    apartment_count: types.optional(types.maybeNull(types.number), null),
    area_average_apartment: types.optional(types.maybeNull(types.number), null),
    area_basements: types.optional(types.maybeNull(types.number), null),
    area_garret: types.optional(types.number, 0),
    area_heating: types.optional(types.maybeNull(types.number), null),
    area_living: types.optional(types.maybeNull(types.number), null),
    area_not_living: types.optional(types.maybeNull(types.number), null),
    area_overall: types.optional(types.maybeNull(types.number), null),
    area_parking: types.optional(types.maybeNull(types.number), null),
    area_roof: types.optional(types.maybeNull(types.number), null),
    area_tech: types.optional(types.number, 0),
    area_total: types.optional(types.number, 0),
    area_total_auto: types.optional(types.maybeNull(types.number), null),
    attached_courts: types.optional(
      types.array(types.frozen<{ [key: string]: any }>()),
      []
    ),
    build_date: types.optional(types.maybeNull(types.string), null),
    bulk: types.optional(types.maybeNull(types.string), null),
    cadastral_number: types.optional(types.maybeNull(types.string), null),
    capital_repair_date: types.optional(types.maybeNull(types.string), null),
    category: types.optional(types.maybeNull(types.string), null),
    default_room_height: types.optional(types.number, 0),
    developer: types.optional(types.frozen<{ [key: string]: any }>(), {}),
    engineering_status: types.optional(types.maybeNull(types.string), null),
    fias_addrobjs: types.optional(types.array(types.string), []),
    fias_house_guid: types.optional(types.string, ''),
    fias_street_guid: types.optional(types.string, ''),
    floor_count: types.optional(types.maybeNull(types.string), null),
    from_csv: types.optional(types.boolean, false),
    fund_id: types.optional(types.maybeNull(types.string), null),
    fund_type: types.optional(types.maybeNull(types.number), null),
    gis_collective: types.optional(types.maybeNull(types.boolean), null),
    gis_fias: types.optional(types.maybeNull(types.string), null),
    gis_metering_end: types.optional(types.maybeNull(types.number), null),
    gis_metering_start: types.optional(types.maybeNull(types.number), null),
    gis_pay_day: types.optional(types.maybeNull(types.number), null),
    gis_pay_doc_day: types.optional(types.maybeNull(types.number), null),
    gis_uid: types.optional(types.maybeNull(types.string), null),
    harvested_region_actual: types.optional(
      types.maybeNull(types.number),
      null
    ),
    harvested_region_standard: types.optional(
      types.maybeNull(types.number),
      null
    ),
    harvested_stairs_actual: types.optional(
      types.maybeNull(types.number),
      null
    ),
    harvested_stairs_standard: types.optional(
      types.maybeNull(types.number),
      null
    ),
    has_plumbing: types.optional(types.boolean, false),
    has_sewage: types.optional(types.maybeNull(types.boolean), null),
    heating_contract: types.optional(
      types.frozen<{ [key: string]: any }>(),
      {}
    ),
    heating_type: types.optional(types.maybeNull(types.string), null),
    hws_type: types.optional(types.string, ''),
    id: types.optional(types.string, ''),
    info: types.optional(types.frozen<{ [key: string]: any }>(), {}),
    is_allowed_meters: types.optional(types.maybeNull(types.boolean), null),
    is_cultural_heritage: types.optional(types.boolean, false),
    kladr: types.optional(types.string, ''),
    lift_count: types.optional(types.maybeNull(types.number), null),
    management_contract: types.optional(
      types.maybeNull(types.frozen<{ [key: string]: any }>()),
      null
    ),
    number: types.optional(types.string, ''),
    offsets_updated_at: types.optional(types.maybeNull(types.string), null),
    overhaul_code: types.optional(types.maybeNull(types.string), null),
    parking_count: types.optional(types.maybeNull(types.number), null),
    point: types.optional(types.frozen<{ [key: string]: any }>(), {}),
    porches: types.optional(types.array(PorchModel), []),
    print_bills_type: types.optional(types.maybeNull(types.string), null),
    protocol_doc: types.optional(types.frozen<{ [key: string]: any }>(), {}),
    service_date: types.optional(types.maybeNull(types.string), null),
    setl_home_address: types.optional(types.maybeNull(types.string), ''),
    settings: types.optional(types.frozen<{ [key: string]: any }>(), {}),
    short_address: types.optional(types.string, ''),
    stair_count: types.optional(types.maybeNull(types.number), null),
    stove_type: types.optional(types.maybeNull(types.string), null),
    street: types.optional(types.string, ''),
    street_only: types.optional(types.string, ''),
    structure: types.optional(types.string, ''),
    type: types.optional(types.maybeNull(types.string), null),
    zip_code: types.optional(types.maybeNull(types.string), ''),
    _redmine: types.optional(types.maybeNull(types.string), ''),
    _sb_cached: types.optional(types.maybeNull(types.string), ''),
  })
  .views((self) => ({
    // Общая информация о доме, приведенная к типу IHouse
    get dataIHouseFormat(): IHouse {
      return {
        address: self.address,
        fias_addrobjs: cloneDeep(self.fias_addrobjs),
        id: self.id,
      };
    },
    get allLifts() {
      return self.porches.reduce(
        (acc: ILift[], porch: IPorch) => acc.concat(porch.lifts),
        []
      );
    },
    get allStandpipes() {
      return self.porches.reduce(
        (acc: IStandpipe[], porch: IPorch) => acc.concat(porch.standpipes),
        []
      );
    },
    getPorchIndexByArea(area_number: number) {
      let currentApartmentCount = 0;

      for (let i = 0; i < self.porches.length; i++) {
        currentApartmentCount +=
          (self.porches[i].area_count || 0) * (self.porches[i].max_floor || 0);

        if (currentApartmentCount >= area_number) {
          return i; // Возвращаем индекс подъезда
        }
      }

      return -1;
    },
  }));

const ProviderHouseModel = types.model('ProviderHouseModel', {
  address: types.string,
  house_id: types.string,
});

export const HousesStore = types
  .model({
    loading: types.optional(RequestsModel, {}),
    houseData: types.optional(HouseDataModel, {}),
    providerHousesById: types.optional(
      types.map(types.array(ProviderHouseModel)),
      {}
    ),
  })
  .volatile(() => ({
    houseDataResultsGeneration: 0,
    houseDataLoadingGeneration: 0,
  }))
  .actions((self) => ({
    getHouseData: flow(function* (id: string, shouldApply?: () => boolean) {
      const resultsGeneration = self.houseDataResultsGeneration;
      const loadingGeneration = self.houseDataLoadingGeneration;
      const canApply = () =>
        resultsGeneration === self.houseDataResultsGeneration &&
        (shouldApply?.() ?? true);
      const canFinalize = () =>
        loadingGeneration === self.houseDataLoadingGeneration;

      try {
        self.loading.houseData.loadingStates();

        const response = yield Houses.GetHouseById(id);

        if (canApply()) {
          self.houseData = response;
          self.loading.houseData.successStates();
        }

        return self.houseData;
      } catch (e) {
        if (canApply()) {
          self.loading.houseData.errorStates();
          console.error(e);
        }
      } finally {
        if (canFinalize()) {
          self.loading.houseData.finallyStates();
        }
      }
    }),
    clearHouseData() {
      self.houseDataResultsGeneration += 1;
      self.houseDataLoadingGeneration += 1;
      self.loading.houseData = cast({});
      self.houseData = cast({});
    },
    getProviderHouses: flow(function* (provider: string) {
      try {
        self.loading.providerHousesById.loadingStates();
        const response: IObject = yield Houses.GetProviderHouses({ provider });
        self.providerHousesById.set(provider, response.data);
        self.loading.providerHousesById.successStates();

        return response;
      } catch (e) {
        self.loading.providerHousesById.errorStates();
        console.error(e);
      } finally {
        self.loading.providerHousesById.finallyStates();
      }
    }),
  }));
